// Considered the retired ContextLevelEditorMolecule (replaced during this feature's
// legacy cutover): rejected because the inspector is a full organism composing
// direction, repeat, filter, sort, segment and enrichment sections that the old
// molecule did not provide and could not be extended to cover.
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import { InputNumberAtom } from '../../atoms/InputNumber/InputNumberAtom';
import TickSliderAtom from '../../atoms/TickSlider/TickSliderAtom';
import SortEditorMolecule from '../../molecules/SortEditor/SortEditorMolecule';
import SwitchAtom from '../../atoms/Switch/SwitchAtom';
import ContextSegmentInspectorMolecule from '../../molecules/ContextSegmentInspector/ContextSegmentInspectorMolecule';
import SegmentPanelMolecule from '../../molecules/SegmentPanel/SegmentPanelMolecule';
import UnifiedFilterTreeMolecule from '../../molecules/UnifiedFilterTree/UnifiedFilterTreeMolecule';
import { withStableFilterGroupIds } from '../../../utils/filterGroupIds';
import { seedLeavesIntoGroups, topLevelRowToLeaf } from '../../../utils/filterGroupSeeding';
import { simpleGroupToUnified, unifiedGroupToSimple } from '../../../utils/contextFlowSerialization';
import AdvancedFilterEditorOrganism from '../AdvancedFilterEditor/AdvancedFilterEditorOrganism';
import type { ContextFlowLevel, ContextFlowSegment } from '../../../types/contextFlow';
import type { FilterState } from '../../../types/filterState';
import type { PropertyOption } from '../../molecules/PropertyFilter';
import type { PerspectiveOption } from '../../molecules/PerspectiveFilter';
import type { RelationshipOption } from '../../molecules/RelationshipFilter';
import type { ArcTemplateOption } from '../../../hooks/useArcTemplates';
import type { OrgAvailableTypeSchema } from 'types/schema';
import type { FilterGroup, FilterGroupLeaf } from 'types/filters';
import { nextFilterId, withStableIds } from '../../../utils/ids';
import { cfGroupStats } from '../../../utils/cfGroup';
import './ContextLevelInspectorOrganism.scss';

const EMPTY_FILTER: FilterState = { propertyFilters: [], perspectiveFilters: [] };

/** Monotonic counter — guarantees unique segment IDs even within the same millisecond. */
let segIdCounter = 0;
const nextSegmentId = (): string =>
  `seg_${Date.now().toString(36)}_${(++segIdCounter).toString(36)}`;

export interface ContextLevelInspectorOrganismProps {
  level: ContextFlowLevel;
  order: number;
  onChange: (updated: ContextFlowLevel) => void;
  onDelete: (id: string) => void;
  onDuplicate: (id: string) => void;
  readOnly?: boolean;
  /** Properties available for the advanced filter editor. */
  availableProperties?: PropertyOption[];
  /** Unscoped org-wide catalog powering the advanced editor's scope-aware rail. */
  orgWideProperties?: PropertyOption[];
  /** This level's eligible schema `system_name`s — the rail's fallback scope. */
  scopeTypes?: string[];
  /** Perspectives available for the advanced filter editor. */
  availablePerspectives?: PerspectiveOption[];
  /** Relationships available for the filter editor (SC4). */
  availableRelationships?: RelationshipOption[];
  /** Full schema set for the sphere family/type visual picker (SC6). */
  familyTypeSchemas?: OrgAvailableTypeSchema[];
  /** Published arc templates for the arc filter lane (SC5). Omit/empty hides the Arcs section. */
  arcTemplates?: ArcTemplateOption[];
  /** Props for PerspectiveSearch (assignee narrowing) — assignee filtering disabled if not provided. */
  perspectiveSearchProps?: {
    schemaSystemName: string;
    sphereId: string;
    direction?: 'outwards' | 'inwards' | 'both';
    hops?: number;
  };
  /**
   * The real six-lane catalog (`useUnifiedFilterCatalog`, wired by the host
   * page against its own `apiClients`/schemas — no fetch coupling here).
   * Forwarded verbatim to the advanced dialog's `UnifiedFilterTreeMolecule`
   * (and to `SegmentPanelMolecule`'s own advanced dialog) as its "Add
   * condition" picker; omitted renders the tree's add-condition button as a
   * no-op skeleton (see `FilterGroupNodeMolecule`).
   */
  renderCatalog?: (opts: { onPick: (leaf: FilterGroupLeaf) => void; onClose: () => void }) => React.ReactNode;
}


const ContextLevelInspectorOrganism: React.FC<ContextLevelInspectorOrganismProps> = ({
  level,
  order,
  onChange,
  onDelete,
  onDuplicate,
  readOnly = false,
  availableProperties = [],
  orgWideProperties,
  scopeTypes,
  availablePerspectives = [],
  availableRelationships = [],
  familyTypeSchemas = [],
  arcTemplates = [],
  perspectiveSearchProps,
  renderCatalog,
}) => {
  const { t } = useTranslation();
  const [advancedOpen, setAdvancedOpen] = useState(false);
  /** ID of the segment whose `SegmentPanelMolecule` drill-in overlay is open (SC4). */
  const [segEditId, setSegEditId] = useState<string | null>(null);

  const patch = useCallback(
    (partial: Partial<ContextFlowLevel>) => onChange({ ...level, ...partial }),
    [level, onChange]
  );

  const handleAddSegment = useCallback(() => {
    const newSeg: ContextFlowSegment = {
      id: nextSegmentId(),
      label: { default: t('context.editor.inspector.segments.newSegmentLabel') },
    };
    patch({ segments: [...(level.segments ?? []), newSeg] });
    setSegEditId(newSeg.id);
  }, [level.segments, patch, t]);

  // ── Level Filters — the embedded tree (uf-simple / D1) ──────────────────────
  // everySurfaceMountsTheSameTree: no bespoke row list, no full-screen drill-in
  // overlay — a single `UnifiedFilterTreeMolecule` mount, matching every other
  // Phase 5 host. Top-level `propertyFilters` fold into the root's own leaves
  // (an AND group gets them appended; an OR root gets a new leading AND group),
  // mirroring `AdvancedFilterEditorOrganism.deriveRoot`/`seedGroups` and
  // `ApplicabilityEditorMolecule.rootGroup` so every host reads one filter
  // shape the same way. Sphere type/family and perspective/relationship/arc
  // are authored as property rows here (EAINT-725 #Q6, one composition model)
  // — there is no separate lane array to preserve verbatim, unlike applicability.
  const filterState = level.filters ?? EMPTY_FILTER;
  const propertyFilters = useMemo(
    () => withStableIds(filterState.propertyFilters ?? []),
    [filterState]
  );

  const rootGroup = useMemo<FilterGroup>(() => {
    const groups = filterState.filterGroups ?? [];
    const seededGroups = seedLeavesIntoGroups(groups, propertyFilters.map(topLevelRowToLeaf));
    const stableGroups = withStableFilterGroupIds(seededGroups);
    if (stableGroups.length === 1) return simpleGroupToUnified(stableGroups[0]);
    return {
      id: nextFilterId(),
      operator: 'and',
      expressions: [],
      groups: stableGroups.map(simpleGroupToUnified),
    };
  }, [filterState.filterGroups, propertyFilters]);

  const filterCount = useMemo(() => cfGroupStats(rootGroup).conditions, [rootGroup]);

  const handleTreeChange = useCallback(
    (next: FilterGroup) => {
      patch({ filters: { ...filterState, propertyFilters: [], filterGroups: [unifiedGroupToSimple(next)] } });
    },
    [filterState, patch]
  );

  // ── Segments drill-in (SC4) ──────────────────────────────────────────────────
  const segments = level.segments ?? [];
  const selectedSegment = useMemo(
    () => (segEditId != null ? segments.find((s) => s.id === segEditId) ?? null : null),
    [segEditId, segments]
  );
  const selectedSegmentIndex = selectedSegment ? segments.indexOf(selectedSegment) : -1;

  const patchSegment = useCallback(
    (id: string, partial: Partial<ContextFlowSegment>) => {
      patch({ segments: segments.map((s) => (s.id === id ? { ...s, ...partial } : s)) });
    },
    [segments, patch]
  );

  const removeSegmentById = useCallback(
    (id: string) => {
      patch({ segments: segments.filter((s) => s.id !== id) });
      setSegEditId((prev) => (prev === id ? null : prev));
    },
    [segments, patch]
  );

  const handleAdvancedApply = useCallback(
    (filters: FilterState) => {
      patch({ filters });
      setAdvancedOpen(false);
    },
    [patch]
  );

  const directionOptions = [
    {
      value: 'outwards' as const,
      label: t('context.editor.inspector.direction.outwards'),
      icon: 'pi pi-arrow-right',
    },
    {
      value: 'inwards' as const,
      label: t('context.editor.inspector.direction.inwards'),
      icon: 'pi pi-arrow-left',
    },
  ];

  if (advancedOpen) {
    return (
      <AdvancedFilterEditorOrganism
        filter={level.filters ?? { propertyFilters: [], perspectiveFilters: [] }}
        onApply={handleAdvancedApply}
        onBack={() => setAdvancedOpen(false)}
        label={`${t('context.editor.inspector.level.eyebrow', { n: order })} · ${
          level.label || t('context.editor.canvas.untitledLevel')
        }`}
        availableProperties={availableProperties}
        orgWideProperties={orgWideProperties}
        scopeTypes={scopeTypes}
        availablePerspectives={availablePerspectives}
        availableRelationships={availableRelationships}
        arcTemplates={arcTemplates}
        familyTypeSchemas={familyTypeSchemas}
        perspectiveSearchProps={perspectiveSearchProps}
        renderCatalog={renderCatalog}
        readOnly={readOnly}
      />
    );
  }

  return (
    <div
      className={`context-level-inspector-organism${readOnly ? ' context-level-inspector-organism--readonly' : ''}`}
      data-testid="context-level-inspector"
    >
      {/* Inspector header */}
      <div className="context-level-inspector-organism__header">
        <span className="context-level-inspector-organism__order-badge" aria-hidden="true">
          {order}
        </span>
        <div className="context-level-inspector-organism__title-group">
          <div className="context-level-inspector-organism__eyebrow">
            {t('context.editor.inspector.level.eyebrow', { n: order })}
          </div>
          <div className="context-level-inspector-organism__title">
            {level.label || t('context.editor.canvas.untitledLevel')}
          </div>
        </div>
        {!readOnly && (
          <>
            <button
              type="button"
              className="context-level-inspector-organism__action-btn"
              aria-label={t('context.editor.inspector.level.duplicate')}
              onClick={() => onDuplicate(level.id)}
            >
              <i className="pi pi-copy" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="context-level-inspector-organism__action-btn context-level-inspector-organism__action-btn--delete"
              aria-label={t('context.editor.inspector.level.delete')}
              onClick={() => onDelete(level.id)}
            >
              <i className="pi pi-trash" aria-hidden="true" />
            </button>
          </>
        )}
      </div>

      {/* Body — swaps between filter editor (SC1), segment panel (SC4), and
          normal level sections. The header above remains visible in all states. */}
      <div className="context-level-inspector-organism__body">
        {/* Segment drill-in swaps the body; the filter drill-in does NOT —
            it renders as an absolutely-positioned sibling below so Sort and
            Segments stay mounted at the same DOM position while editing (SC1). */}
        {selectedSegment != null ? (
          <SegmentPanelMolecule
            segment={selectedSegment}
            index={selectedSegmentIndex}
            levelColor={level.color}
            levelSortBy={level.sort?.[0]?.property}
            levelSortDir={level.sort?.[0]?.dir}
            availableProperties={availableProperties}
            orgWideProperties={orgWideProperties}
            scopeTypes={scopeTypes}
            availablePerspectives={availablePerspectives}
            availableRelationships={availableRelationships}
            arcTemplates={arcTemplates}
            familyTypeSchemas={familyTypeSchemas}
            perspectiveSearchProps={perspectiveSearchProps}
            renderCatalog={renderCatalog}
            onChangeSegment={(partial) => patchSegment(selectedSegment.id, partial)}
            onRemove={() => removeSegmentById(selectedSegment.id)}
            onBack={() => setSegEditId(null)}
            readOnly={readOnly}
          />
        ) : (
          <>
            {/* Direction */}
            <div className="context-level-inspector-organism__section">
              <div className="context-level-inspector-organism__section-label">
                {t('context.levelEditor.direction')}
              </div>
              <SegmentedControlAtom
                options={directionOptions}
                value={level.direction}
                onChange={(v) => patch({ direction: v as 'outwards' | 'inwards' })}
                mode="single"
                size="small"
                ariaLabel={t('context.levelEditor.direction')}
              />
              <div className="context-level-inspector-organism__section-hint">
                {level.direction === 'outwards'
                  ? t('context.editor.inspector.direction.outwardsDesc')
                  : t('context.editor.inspector.direction.inwardsDesc')}
              </div>
            </div>

            {/* Repeat */}
            <div className="context-level-inspector-organism__section">
              <div className="context-level-inspector-organism__section-label-row">
                <div className="context-level-inspector-organism__section-label">
                  {t('context.levelEditor.repeat')}
                </div>
                <span className="context-level-inspector-organism__repeat-value">
                  {level.repeat ?? 1}×
                </span>
              </div>
              <TickSliderAtom
                value={level.repeat ?? 1}
                min={1}
                max={5}
                onChange={(v) => patch({ repeat: v })}
                disabled={readOnly}
                ariaLabel={t('context.levelEditor.repeat')}
                className="context-level-inspector-organism__repeat-slider"
              />
            </div>

            {/* Filters */}
            <div className="context-level-inspector-organism__section">
              <div className="context-level-inspector-organism__section-label-row">
                <div className="context-level-inspector-organism__section-label">
                  {t('context.levelEditor.filters')}
                </div>
                {filterCount > 0 && (
                  <span className="context-level-inspector-organism__filter-count-badge">
                    {filterCount} {t('context.editor.inspector.level.filtersActive')}
                  </span>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    className="context-level-inspector-organism__advanced-btn"
                    aria-label={t('context.editor.inspector.level.advanced')}
                    onClick={() => setAdvancedOpen(true)}
                  >
                    <i className="pi pi-sliders-h" aria-hidden="true" />
                    {t('context.editor.inspector.level.advanced')}
                  </button>
                )}
              </div>
              {/* everySurfaceMountsTheSameTree — the same canonical tree every other
                  surface mounts (property leaves and nested AND/OR groups in one
                  composer); editing a row expands it inline (EditableFilterRowMolecule's
                  own self-managed drill, no `onEdit`), no separate overlay. Sphere Type
                  (`type`) and Sphere Family (`label`) are authored as property filters —
                  their value editor is the SphereTypeSelect chip selector; perspective /
                  relationship / arc likewise author as property rows (EAINT-725 #Q6, one
                  composition model), so there is no separate lane array to fold in. */}
              <UnifiedFilterTreeMolecule
                value={rootGroup}
                onChange={handleTreeChange}
                availableProperties={availableProperties}
                arcTemplates={arcTemplates}
                availableRelationships={availableRelationships}
                availablePerspectives={availablePerspectives}
                familyTypeSchemas={familyTypeSchemas}
                perspectiveSearchProps={perspectiveSearchProps}
                renderCatalog={renderCatalog}
                readOnly={readOnly}
                emptyNote={t('context.editor.inspector.level.filtersEmpty')}
                datumScope="context-level-filter-tree"
                container="ui-tree"
                className="context-level-inspector-organism__filter-tree"
                dnd
                defaultOpen
              />
            </div>

            {/* Sort — an ordered list of clauses; primary first. */}
            <div className="context-level-inspector-organism__section">
              <SortEditorMolecule
                value={level.sort ?? []}
                availableProperties={availableProperties}
                onChange={(sort) => patch({ sort })}
                readOnly={readOnly}
              />
              <div className="context-level-inspector-organism__section-hint">
                {t('context.editor.inspector.level.sortHelp')}
              </div>
            </div>

            {/* Result limit */}
            <div className="context-level-inspector-organism__section">
              <div className="context-level-inspector-organism__section-label">
                {t('context.editor.inspector.level.resultLimit')}
              </div>
              <div className="context-level-inspector-organism__result-limit-row">
                <InputNumberAtom
                  value={level.pageSize ?? null}
                  onChange={(v) => {
                    const next = v ?? undefined;
                    if (next !== level.pageSize) patch({ pageSize: next });
                  }}
                  disabled={readOnly}
                  min={1}
                  max={500}
                  type="integer"
                  placeholder={t('context.editor.inspector.level.resultLimitPlaceholder')}
                  className="context-level-inspector-organism__limit-input"
                />
                <span className="context-level-inspector-organism__limit-help">
                  {t('context.editor.inspector.level.resultLimitHelp')}
                </span>
              </div>
            </div>

            {/* Behaviour */}
            <div className="context-level-inspector-organism__section">
              <div className="context-level-inspector-organism__section-label">
                {t('context.editor.inspector.behaviour.title')}
              </div>
              <div className="context-level-inspector-organism__behaviour-row" data-testid="behaviour-lazy-load">
                <SwitchAtom
                  label={t('context.editor.inspector.behaviour.lazyLoadHelp')}
                  checked={level.enrichment?.lazy_load ?? false}
                  onChange={(checked) =>
                    patch({ enrichment: { ...level.enrichment, lazy_load: checked } })
                  }
                  disabled={readOnly}
                  inputId={`lazy-load-${level.id}`}
                />
              </div>
            </div>

            {/* Segments */}
            <div className="context-level-inspector-organism__section">
              <ContextSegmentInspectorMolecule
                segments={segments}
                levelColor={level.color}
                onAddSegment={handleAddSegment}
                onEditSegment={setSegEditId}
                readOnly={readOnly}
              />
            </div>

          </>
        )}
      </div>
    </div>
  );
};

export default ContextLevelInspectorOrganism;
